import { StrategyType, NumericMarketData } from "../types";

export const stockTriggers = [
  {
    name: "rsi_overbought",
    label: "RSI > 70",
    strategies: [
      StrategyType.CashExtraction,
      StrategyType.Hedging,
      StrategyType.SyntheticYield,
      StrategyType.PutsSpreads,
      StrategyType.Puts,
    ],
    info: "RSI > 70 indicates overbought conditions, suggesting a potential price pullback. Option strategies that could work include Cash Extraction to limit losses on the downside, Synthetic Yield to generate income and create a buffer on pullbacks, and using Puts and Put Spreads to hedge the downside.",
    value: (data: NumericMarketData) => {
      return data?.["Overbought and Oversold"] > 70;
    },
  },
  {
    name: "rsi-oversold",
    label: "RSI < 30",
    strategies: [StrategyType.Calls, StrategyType.CallsSpreads],
    info: "RSI < 30 indicates oversold conditions, suggesting a potential price rally. Option strategies that could work include Calls and Call Spreads to benefit from a rally.",
    value: (data: NumericMarketData) => {
      return data?.["Overbought and Oversold"] < 30;
    },
  },
  {
    name: "1yr-high",
    label: "1% off 1yr high",
    strategies: [
      StrategyType.CashExtraction,
      StrategyType.Hedging,
      StrategyType.SyntheticYield,
      StrategyType.PutsSpreads,
      StrategyType.Puts,
    ],
    info: "Stocks close to their 1yr high could experience a potential price pullback due to resistance levels. Option strategies that could work include Cash Extraction to limit losses on the downside, Synthetic Yield to generate income and create a buffer on pullbacks, and using Puts and Put Spreads to hedge the downside.",
    value: (data: NumericMarketData) => {
      return (
        data?.["Closest to high - 1 year"] >= 0 &&
        data?.["Closest to high - 1 year"] <= 1
      );
    },
  },
  {
    name: "1yr-low",
    label: "1% off 1yr low",
    strategies: [StrategyType.Calls, StrategyType.CallsSpreads],
    info: "Stocks close to their 1yr low could experience a potential rally due to reaching support levels. Option strategies that could work include Calls and Call Spreads to benefit from a rally.",
    value: (data: NumericMarketData) => {
      return (
        data?.["Closest to low - 1 year"] >= 0 &&
        data?.["Closest to low - 1 year"] <= 1
      );
    },
  },
  {
    name: "volume-change",
    label: "Volume Chg > 100%",
    info: "Stocks experiencing sharp increases in Volume could indicate that they are about to move aggressively in one direction or another. Option strategies that could work include Calls and Call Spreads to benefit from a rally.",
    value: (data: NumericMarketData) => {
      return data?.["Volume Change"] > 100;
    },
  },
  {
    name: "rsi-1wk-expected-return-5",
    dataLessLabel: "RSI 1wk Expected Return > 5%",
    strategies: [StrategyType.Calls, StrategyType.CallsSpreads],
    info: "When the stock has historically been at this RSI level, a quant model has shown that the stock has an expected return (positive return probability x avg return) greater than 5% over the next week. Option strategies that could work include 1wk Calls and Call Spreads to benefit from a rally with strikes around the expected return.",
    label: (data: NumericMarketData) => {
      return `RSI 1wk Expected Return > 5% (${(
        (data?.["RSI Win Probability - 1 week"] *
          data?.["RSI Win Price Change - 1 week"]) /
        100
      ).toFixed(1)})`;
    },
    value: (data: NumericMarketData) => {
      return (
        (data?.["RSI Win Probability - 1 week"] *
          data?.["RSI Win Price Change - 1 week"]) /
          100 >
          5 && data?.["RSI Win Probability - 1 week"] > 80
      );
    },
  },
  {
    name: "rsi-1m-expected-return-10",
    strategies: [StrategyType.Calls, StrategyType.CallsSpreads],
    dataLessLabel: "RSI 1m Expected Return > 10%",
    info: "When the stock has historically been at this RSI level, a quant model has shown that the stock has an expected return (positive return probability x avg return) greater than 10% over the next month. Option strategies that could work include 1m Calls and Call Spreads to benefit from a rally with strikes around the expected return.",
    label: (data: NumericMarketData) => {
      return `RSI 1m Expected Return > 10% (${(
        (data?.["RSI Win Probability - 1 month"] *
          data?.["RSI Win Price Change - 1 month"]) /
        100
      ).toFixed(1)})`;
    },
    value: (data: NumericMarketData) => {
      return (
        (data?.["RSI Win Probability - 1 month"] *
          data?.["RSI Win Price Change - 1 month"]) /
          100 >
          10 && data?.["RSI Win Probability - 1 month"] > 80
      );
    },
  },
  {
    name: "rsi-3m-expected-return-20",
    strategies: [StrategyType.Calls, StrategyType.CallsSpreads],
    dataLessLabel: "RSI 3m Expected Return > 20%",
    info: "When the stock has historically been at this RSI level, a quant model has shown that the stock has an expected return (positive return probability x avg return) greater than 20% over the next 3m. Option strategies that could work include 3m Calls and Call Spreads to benefit from a rally with strikes around the expected return.",
    label: (data: NumericMarketData) => {
      return `RSI 3m Expected Return > 20% (${(
        (data?.["RSI Win Probability - 3 months"] *
          data?.["RSI Win Price Change - 3 months"]) /
        100
      ).toFixed(1)})`;
    },
    value: (data: NumericMarketData) => {
      return (
        (data?.["RSI Win Probability - 3 months"] *
          data?.["RSI Win Price Change - 3 months"]) /
          100 >
          20 && data?.["RSI Win Probability - 3 months"] > 80
      );
    },
  },
  {
    name: "distance-to-target-price",
    strategies: [StrategyType.Calls, StrategyType.CallsSpreads],
    dataLessLabel: "Distance to Target Price > 50%",
    label: "Distance to Target Price > 50%",
    info: "When the target price for a stock is over 50% away from the current price, the stock could experience a rally. Option strategies that could work include Calls and Call Spreads to benefit from a rally.",
    value: (data: NumericMarketData) => {
      return data?.["Distance from Target Price"] > 50;
    },
  },
  {
    name: "golden-cross-50-200-occured",

    label: "Golden cross (50/200) has just occurred",
    info: "When a stock experiences a golden cross, there could be a rally. Option strategies that could work include Calls and Call Spreads to benefit from a rally.",
    value: (data: NumericMarketData) => {
      const sma50 = data?.["Simple Moving Average - 50 day"];
      const sma200 = data?.["Simple Moving Average - 200 day"];
      const price = data.price;
      const value = sma50 && sma200 && (sma50 - sma200) / sma200;
      const betweenThreshold = 0.02 >= value && value >= 0;
      const priceAbove = price > sma50;
      return betweenThreshold && priceAbove;
    },
  },
  {
    name: "golden-cross-50-200-close",
    label: `Golden cross (50/200) is close to potentially occuring`,
    info: "When a stock experiences a golden cross, there could be a rally. Option strategies that could work include Calls and Call Spreads to benefit from a rally.",
    value: (data: NumericMarketData) => {
      const sma50 = data?.["Simple Moving Average - 50 day"];
      const sma200 = data?.["Simple Moving Average - 200 day"];
      const price = data.price;
      const value = sma50 && sma200 && (sma50 - sma200) / sma200;
      const betweenThreshold = 0 > value && value >= -0.02;
      const priceAbove = price > sma50;
      return betweenThreshold && priceAbove;
    },
  },
  {
    name: "death-cross-50-200-occured",
    label: "Death cross (50/200) has just occurred",
    info: "When a stock experiences a death cross, there could be a pullback. Option strategies that could work include Cash Extraction to limit losses on the downside, Synthetic Yield to generate income and create a buffer on pullbacks, and using Puts and Put Spreads to hedge the downside.",
    value: (data: NumericMarketData) => {
      const sma50 = data?.["Simple Moving Average - 50 day"];
      const sma200 = data?.["Simple Moving Average - 200 day"];
      const price = data.price;
      const value = sma50 && sma200 && (sma50 - sma200) / sma200;
      const betweenThreshold = 0 >= value && value >= -0.02;
      const priceBelow = price < sma50;
      return betweenThreshold && priceBelow;
    },
  },
  {
    name: "death-cross-50-200-close",
    label: `Death cross (50/200) is close to potentially occuring`,
    info: "When a stock experiences a death cross, there could be a pullback. Option strategies that could work include Cash Extraction to limit losses on the downside, Synthetic Yield to generate income and create a buffer on pullbacks, and using Puts and Put Spreads to hedge the downside.",
    value: (data: NumericMarketData) => {
      const sma50 = data?.["Simple Moving Average - 50 day"];
      const sma200 = data?.["Simple Moving Average - 200 day"];
      const price = data.price;
      const value = sma50 && sma200 && (sma50 - sma200) / sma200;
      const betweenThreshold = 0.02 >= value && value > 0;
      const priceBelow = price < sma50;
      return betweenThreshold && priceBelow;
    },
  },

  /*  {
    name: "50d-sma-above-100d-sma-2pct",
    label: "Golden cross (50/100) has just occured",
    value: (data: NumericMarketData) => {
      const price = data?.price;
      const sma50 = data?.["Simple Moving Average - 50 day"];
      const sma100 = data?.["Simple Moving Average - 100 day"];
      const value = sma50 && sma100 && (sma50 - sma100) / sma100;
      return 0.02 >= value && value >= 0;
    },
  },
  {
    name: "50d-sma-below-100d-sma-2pct",
    label: "Golden cross (50/100) is close to potentially occuring",
    value: (data: NumericMarketData) => {
      const sma50 = data?.["Simple Moving Average - 50 day"];
      const sma100 = data?.["Simple Moving Average - 100 day"];
      const value = sma50 && sma100 && (sma50 - sma100) / sma100;
      return 0 >= value && value >= -0.02;
    },
  },

  {
    name: "100d-sma-above-200d-sma-2pct",
    label: "Golden cross (100/200) has just occured",
    value: (data: NumericMarketData) => {
      const sma100 = data?.["Simple Moving Average - 100 day"];
      const sma200 = data?.["Simple Moving Average - 200 day"];
      const value = sma100 && sma200 && (sma100 - sma200) / sma200;
      return value && 0.02 >= value && value >= 0;
    },
  },
  {
    name: "100d-sma-below-200d-sma-2pct",
    label: "Golden cross (100/200) is close to potentially occuring",
    value: (data: NumericMarketData) => {
      const sma100 = data?.["Simple Moving Average - 100 day"];
      const sma200 = data?.["Simple Moving Average - 200 day"];
      const value = sma100 && sma200 && (sma100 - sma200) / sma200;
      return 0 >= value && value >= -0.02;
    },
  }, */
];
